import { yupResolver } from '@hookform/resolvers/yup';
import {
	createRawRichTextContentStateWithHtmlStringValue,
	CustomPageType,
	FooterCustomization,
	getContentHtmlStringValueFromRawRichTextContentState,
	IOperationResultNoValue,
} from '@ViewModels';
import { RichTextEditor } from '@levitate/rich-text';
import equal from 'fast-deep-equal';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useDeleteBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useDeleteBlogStyleMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { Field, FieldLabel } from '../../../../../web/components-v2/Field';
import { Input } from '../../../../../web/components-v2/Input';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../../web/components/ConfirmationDialog';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import { ColorField } from '../components/ColorField';
import { NavLinkBuilder } from '../components/NavLinkBuilder';
import { SettingCard } from '../components/SettingCard';
import { applyPresetToFooterForm } from '../applyPreset';
import { StylePreset } from '../presets';
import {
	DEFAULT_FOOTER_BACKEND_TEXT_VALUES,
	DEFAULT_FOOTER_FORM_VALUES,
	DEFAULT_SIMPLE_LINK_VALUES,
	isDefaultFooterText,
	MINIMUM_FOOTER_LINKS,
} from './constants';
import { FeatureToggleSection } from './FeatureToggleSection';
import { footerSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface FooterDesignFormProps {
	customContent?: FooterCustomization;
	onFormDirty: () => void;
	onUpdateSuccess: () => void;
	onSavingChange?: (saving: boolean) => void;
}

export interface FooterDesignFormRef {
	applyPreset: (preset: StylePreset) => void;
	submit?: () => void;
}

/** HTML to seed the editor with — empty for absent or default text, so the placeholder shows. */
const getFooterSeedHtml = (customContent?: FooterCustomization): string => {
	const document = customContent?.text?.document;
	if (document && !isDefaultFooterText(document)) {
		return getContentHtmlStringValueFromRawRichTextContentState(customContent?.text);
	}
	return '';
};

export const FooterDesignForm = forwardRef<FooterDesignFormRef, FooterDesignFormProps>(
	({ customContent, onFormDirty, onUpdateSuccess, onSavingChange }, ref) => {
		const errorMessages = useErrorMessages();
		const { account, accountId, impersonationContext } = useAccountContext();
		const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

		// Determine which features are available for this account
		const blogFeature = account?.features?.blogFeature;
		const blogFeatureAvailable = !!blogFeature?.enabled && !blogFeature?.dudaSiteId;
		const surveyFeatureAvailable = !!account?.features?.surveys?.enabled;
		const meetingFeatureAvailable = !!account?.features?.meetingScheduler?.enabled;
		const filesFeatureAvailable = !!account?.features?.files?.enabled && surveyFeatureAvailable;

		const form = useForm<FooterCustomization>({
			defaultValues: DEFAULT_FOOTER_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(footerSchema),
		});

		const {
			formState: { errors, isDirty },
			getValues,
			handleSubmit,
			register,
			setValue,
			watch,
		} = form;

		// Watch color values for live updating
		const backgroundColor = watch('backgroundColor');
		const textColor = watch('textColor');
		const linkColor = watch('linkColor');

		// Watch enabledFor for feature toggles
		const enabledFor = watch('enabledFor');

		const handleEnabledForChange = useCallback(
			(newEnabledFor: CustomPageType[]) => {
				setValue('enabledFor', newEnabledFor, { shouldDirty: true });
			},
			[setValue]
		);

		// RichTextEditor only reads `initialContent` at mount, so re-seeding (data load, delete) bumps
		// `version` to force a remount; typing writes to the form's `text` field and leaves the seed
		// untouched, so the editor is never remounted mid-edit.
		const [footerSeed, setFooterSeed] = useState(() => ({ html: getFooterSeedHtml(customContent), version: 0 }));
		const reseedFooterEditor = useCallback(
			(html: string) => setFooterSeed(prev => (prev.html === html ? prev : { html, version: prev.version + 1 })),
			[]
		);

		const handleTextChange = (html: string) => {
			setValue('text', createRawRichTextContentStateWithHtmlStringValue(html), { shouldDirty: true });
		};

		const updateFooterMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				form.reset(getValues());
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteFooterMutation = useDeleteBlogStyleMutation({
			onSuccess: () => {
				form.reset(DEFAULT_FOOTER_FORM_VALUES);
				reseedFooterEditor('');
				onUpdateSuccess();
				toast.success('Customization deleted successfully');
				setShowDeleteDialog(false);
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				setShowDeleteDialog(false);
			},
		});

		const onSubmit: SubmitHandler<FooterCustomization> = (data, event) => {
			event?.preventDefault?.();
			event?.stopPropagation?.();

			updateFooterMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const handleDeleteCustomization = () => {
			setShowDeleteDialog(true);
		};

		const handleDeleteConfirm = (_: never, canceled: boolean) => {
			if (!canceled && customContent && customContent.id) {
				deleteFooterMutation.mutate({
					id: customContent.id,
					impersonationContext,
				});
			} else {
				setShowDeleteDialog(false);
			}
		};

		useEffect(() => {
			if (customContent && !equal(customContent, getValues())) {
				Object.entries(customContent).forEach(entry => {
					if (entry[0] === 'links' && entry[1].length <= 0) {
						return;
					}
					if (entry[0] === 'links' && entry[1].length < MINIMUM_FOOTER_LINKS) {
						// links array dynamically generates ui rows
						// minimum of 3 maintains spec
						const newLinksArray = [...entry[1]];
						newLinksArray.length = MINIMUM_FOOTER_LINKS;
						newLinksArray.fill(DEFAULT_SIMPLE_LINK_VALUES, entry[1].length);
						setValue('links', newLinksArray);
						return;
					}
					if (entry[0] === 'text') {
						const documentText = entry[1].document;
						if (documentText && !isDefaultFooterText(documentText)) {
							reseedFooterEditor(getContentHtmlStringValueFromRawRichTextContentState(entry[1]));
							setValue('text', entry[1]);
						}
						return;
					}
					if (entry[0] === 'copyrightInfo') {
						if (entry[1] && entry[1] !== DEFAULT_FOOTER_BACKEND_TEXT_VALUES.copyrightInfo) {
							setValue('copyrightInfo', entry[1]);
						}
						return;
					}
					if (entry[1] !== undefined && entry[1] !== null) {
						setValue(entry[0] as Extract<keyof FooterCustomization, string>, entry[1]);
					}
				});
			}
		}, [customContent, getValues, setValue, reseedFooterEditor]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useEffect(() => {
			onSavingChange?.(updateFooterMutation.isPending);
		}, [updateFooterMutation.isPending, onSavingChange]);

		useImperativeHandle(ref, () => ({
			applyPreset: (preset: StylePreset) => {
				const currentValues = getValues();
				const updatedValues = applyPresetToFooterForm(preset, currentValues);

				Object.entries(updatedValues).forEach(([key, value]) => {
					if (value !== undefined) {
						setValue(key as keyof FooterCustomization, value, { shouldDirty: true });
					}
				});
			},
			submit: () => handleSubmit(onSubmit)(),
		}));

		return (
			<>
				<FormProvider {...form}>
					<TemplateFormWrapper
						chromeless
						title='Customize Footer'
						infoMessage='This styling applies to blogs, surveys, and meetings.'
						isLoading={updateFooterMutation.isPending}
						errors={errors}
						onSubmit={handleSubmit(onSubmit)}
						onDeleteCustomization={handleDeleteCustomization}
						isDeleting={deleteFooterMutation.isPending}
						hasCustomization={!!customContent && !!customContent.id}
					>
						<SettingCard title='Colors'>
							<ColorField
								id='footer-background-color'
								label='Background'
								value={backgroundColor}
								fallback='#ffffff'
								onChange={color => setValue('backgroundColor', color, { shouldDirty: true })}
							/>
							<ColorField
								id='footer-text-color'
								label='Text'
								value={textColor}
								fallback='#000000'
								onChange={color => setValue('textColor', color, { shouldDirty: true })}
							/>
							<ColorField
								id='footer-link-color'
								label='Link'
								value={linkColor}
								fallback='#0066cc'
								onChange={color => setValue('linkColor', color, { shouldDirty: true })}
							/>
						</SettingCard>

						<SettingCard title='Information'>
							<Field>
								<FieldLabel>Footer Text</FieldLabel>
								<RichTextEditor
									key={footerSeed.version}
									initialContent={footerSeed.html}
									placeholder={DEFAULT_FOOTER_BACKEND_TEXT_VALUES.footerText}
									showFormatting
									showLists={false}
									showMergeField={false}
									onChange={handleTextChange}
								/>
							</Field>
							<Field>
								<FieldLabel htmlFor='footer-copyright-info'>Copyright Info</FieldLabel>
								<Input
									id='footer-copyright-info'
									placeholder={DEFAULT_FOOTER_BACKEND_TEXT_VALUES.copyrightInfo}
									{...register('copyrightInfo')}
								/>
							</Field>
						</SettingCard>

						<SettingCard title='Links'>
							<NavLinkBuilder name='links' />
						</SettingCard>

						{(blogFeatureAvailable || surveyFeatureAvailable || meetingFeatureAvailable) && (
							<SettingCard
								title='Apply to features'
								infoMessage='Choose which features should use this custom styling. When disabled, the feature will use default system styling.'
							>
								<FeatureToggleSection
									hideHeading
									enabledFor={enabledFor}
									onEnabledForChange={handleEnabledForChange}
									blogFeatureAvailable={blogFeatureAvailable}
									surveyFeatureAvailable={surveyFeatureAvailable}
									meetingFeatureAvailable={meetingFeatureAvailable}
									filesFeatureAvailable={filesFeatureAvailable}
									disabled={updateFooterMutation.isPending}
								/>
							</SettingCard>
						)}
					</TemplateFormWrapper>
				</FormProvider>
				{showDeleteDialog && (
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{ isOpen: showDeleteDialog, onRequestClose: handleDeleteConfirm }}
						options={DefaultConfirmationOptions}
						title='Are you sure you want to delete this customization? This action cannot be undone.'
					/>
				)}
			</>
		);
	}
);

FooterDesignForm.displayName = 'FooterDesignForm';
